import React, { ReactElement, useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { List, Map } from 'immutable';

import { fetchQueueStatSummary } from 'flow/actions/pbx-actions';
import FavCheckbox from 'flow/components/FavoriteCheckbox';
import Page from 'flow/components/Setting/Page';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { PbxParams } from 'flow/types/Params';
import { routes } from 'flow/types/routes';

import {
    getQueueStatisticsFavorites,
    saveQueueStatisticsFavorites,
} from 'flow/utils/browser-utils';
import {
    getFormattedServiceLevel,
    getServiceLevelsForQueue,
    setServiceLevelsForQueue,
} from 'flow/utils/servicelevel-utils';
import { twMerge } from 'tailwind-merge';
import ServiceLevelTitle from './ServiceLevelTitle';
import PercentageWidget from './statistic-widgets/PercentageWidget';
import TimeWidget from './statistic-widgets/TimeWidget';

const intervalLabels = [
    'Queue.Statistics.Interval.Today',
    'Queue.Statistics.Interval.Week',
    'Queue.Statistics.Interval.Month',
];

const intervals = ['today', 'week', 'month'];

const QueueStatistics = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { openModal } = useModal();

    const { entityKey } = useParams<PbxParams>();

    const title = i18next.t('Queue.Statistics.Header.Title');
    const description = i18next.t('Queue.Statistics.Header.Description');

    const [favorites, setFavorites] = useState(
        List(getQueueStatisticsFavorites(entityKey) || ['answeredCalls']),
    );

    const stats = 'queueStats';
    const answered = 'answeredCalls';
    const received = 'recievedCalls'; // Typo from API

    const serviceLevels = getServiceLevelsForQueue(entityKey);

    useEffect(() => {
        dispatch(fetchQueueStatSummary(entityKey, serviceLevels));
    }, []);

    const statSummary = useAppSelector((state) =>
        state.pbx.getIn(['queues', entityKey, 'statistics']),
    );

    if (statSummary && statSummary.isEmpty()) {
        return <div>{i18next.t('Queue.Statistics.Failed')}</div>;
    }

    const getOnDurationChangedCallback =
        (index: number) => (seconds: number) => {
            serviceLevels[index] = seconds;
            setServiceLevelsForQueue(entityKey, serviceLevels);
        };

    const serviceLevelTitle = (serviceLevel: number, index: number) => (
        <ServiceLevelTitle
            serviceLevel={serviceLevel}
            onDurationChanged={getOnDurationChangedCallback(index)}
        />
    );

    const removeFavorite = (statisticsType: string, saveFavorites: any) => {
        const index = favorites.indexOf(statisticsType);
        const updates = favorites.splice(index, 1);
        saveFavorites(entityKey, updates.toJS());
        // @ts-expect-error
        setFavorites(updates);
    };

    const saveFavourite = (statisticsType: string) => {
        const prevChecked = !!favorites?.find(
            (item: string) => item === statisticsType,
        );

        if (favorites.size < 2) {
            if (!prevChecked) {
                const updates = favorites.push(statisticsType);
                saveQueueStatisticsFavorites(entityKey, updates.toJS());
                setFavorites(updates);
            } else {
                removeFavorite(statisticsType, saveQueueStatisticsFavorites);
            }
        } else if (favorites.size === 2 && prevChecked) {
            removeFavorite(statisticsType, saveQueueStatisticsFavorites);
        } else {
            openModal('confirmationDialog', {
                title: i18next.t('Queue.Statistics.MaximumWidgets.Title'),
                description: i18next.t(
                    'Queue.Statistics.MaximumWidgets.Description',
                ),
            });
        }
    };

    const renderAnswerRatio = (index: number) => {
        const intervalStatistics = statSummary?.getIn(
            [stats, intervals[index]],
            Map(),
        );

        return (
            <PercentageWidget
                successfulEvents={intervalStatistics?.get(answered)}
                allEvents={intervalStatistics?.get(received)}
                strokeColor={getComputedStyle(
                    document.documentElement,
                ).getPropertyValue('--color-huntgroup')}
                trailColor={getComputedStyle(
                    document.documentElement,
                ).getPropertyValue('--color-ornament')}
            />
        );
    };

    const renderAverageWaitTime = (index: number) => {
        const intervalStatistics = statSummary?.getIn(
            [stats, intervals[index]],
            Map(),
        );

        return (
            <TimeWidget
                timeInSeconds={intervalStatistics?.get('avgWaitTime')}
            />
        );
    };

    const renderAverageTalkTime = (index: number) => {
        const intervalStatistics = statSummary?.getIn(
            [stats, intervals[index]],
            Map(),
        );

        return (
            <TimeWidget
                timeInSeconds={intervalStatistics?.get('avgTalkTime')}
            />
        );
    };

    const renderServiceLevel = (index: number, time: string) => {
        const intervalStatistics = statSummary?.getIn(
            [stats, intervals[index]],
            Map(),
        );

        return (
            <PercentageWidget
                successfulEvents={intervalStatistics?.getIn([
                    'serviceLevels',
                    `${time}`,
                ])}
                allEvents={intervalStatistics?.get(received)}
                strokeColor={getComputedStyle(
                    document.documentElement,
                ).getPropertyValue('--color-huntgroup')}
                trailColor={getComputedStyle(
                    document.documentElement,
                ).getPropertyValue('--color-ornament')}
            />
        );
    };

    const renderStatisticsCategory = (
        type: string,
        title: string | ReactElement,
        description: ReactElement,
        renderStatistics: (value: number) => ReactElement,
    ) => {
        // Read straight from the stored favourites on every render. Past the
        // max, saveFavourite only opens the dialog and saves nothing, so this
        // stays false and the star does not move.
        const checked = !!favorites?.find((item: string) => item === type);

        return (
            <div className="flex w-full flex-col items-center justify-center rounded-md border p-3 dark:bg-gray-100">
                <div className="flex w-full">
                    <div className="flex w-full flex-col justify-between gap-y-2">
                        <div className="flex w-full flex-1 items-center justify-center text-xl font-bold">
                            <div className="flex flex-1 justify-center ps-8">
                                {title}
                            </div>

                            <div className="flex justify-end">
                                <FavCheckbox
                                    className={twMerge(
                                        checked ? 'bg-green' : 'bg-gray-200',
                                    )}
                                    color="text-white"
                                    checked={checked}
                                    onChange={() => saveFavourite(type)}
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-center text-base">
                            {description}
                        </div>
                    </div>
                </div>
                <div className="flex w-full flex-wrap justify-evenly gap-2 py-8">
                    {intervalLabels.map((label, index) => (
                        <div className="gap-2" key={index}>
                            <div className="flex justify-center p-1 font-bold text-gray-700">
                                {i18next.t(label)}
                            </div>
                            <div className="flex-end flex">
                                {renderStatistics(index)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const handleBackButton = () => {
        const parentPage =
            location.pathname ===
            generatePath(routes['pbx.queues.entityKey.settings.statistics'], {
                entityKey,
            })
                ? routes['pbx.queues.entityKey.settings']
                : routes['pbx.queues.entityKey'];

        return history.push(generatePath(parentPage, { entityKey }));
    };

    return (
        <Page
            header={i18next.t('Queue.Statistics.Title')}
            onClickBack={handleBackButton}
        >
            <div className="flex flex-col gap-y-4">
                <div className="flex flex-col gap-y-2">
                    <div className="flex items-center justify-center text-xl font-bold">
                        {title}
                    </div>
                    <div className="flex items-center justify-center text-base">
                        {description}
                    </div>
                </div>

                {renderStatisticsCategory(
                    answered,
                    i18next.t('Queue.Statistics.AnswerRatio.Title'),
                    i18next.t('Queue.Statistics.AnswerRatio.Description'),
                    renderAnswerRatio,
                )}

                {renderStatisticsCategory(
                    'avgWaitTime',
                    i18next.t('Queue.Statistics.AverageWaitTime.Title'),
                    i18next.t('Queue.Statistics.AverageWaitTime.Description'),
                    renderAverageWaitTime,
                )}

                {renderStatisticsCategory(
                    'avgTalkTime',
                    i18next.t('Queue.Statistics.AverageTalkTime.Title'),
                    i18next.t('Queue.Statistics.AverageTalkTime.Description'),
                    renderAverageTalkTime,
                )}

                {renderStatisticsCategory(
                    'serviceLevels60',
                    serviceLevelTitle(serviceLevels[0], 0),
                    i18next.t('Queue.Statistics.ServiceLevel.Description', {
                        duration: getFormattedServiceLevel(
                            serviceLevels[0],
                            i18next,
                        ),
                    }),
                    (index: number) =>
                        renderServiceLevel(index, serviceLevels[0]),
                )}

                {renderStatisticsCategory(
                    'serviceLevels120',
                    serviceLevelTitle(serviceLevels[1], 1),
                    i18next.t('Queue.Statistics.ServiceLevel.Description', {
                        duration: getFormattedServiceLevel(
                            serviceLevels[1],
                            i18next,
                        ),
                    }),
                    (index: number) =>
                        renderServiceLevel(index, serviceLevels[1]),
                )}
            </div>
        </Page>
    );
};

export default QueueStatistics;
